import {
  Avatar,
  Box,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { HelpCircle, Search, TrendingUp, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "../../../common/components/Toast";
import PageHeader from "../../../common/components/PageHeader";
import {
  useSupportStats,
  useSupportTickets,
  useUpdateSupportTicket,
} from "../../../support/api/support";
import { StatCard } from "../../shared/components/StatCard";

function Support() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  const {
    supportTickets,
    pagination,
    error: ticketsError,
    refetch: refetchTickets,
  } = useSupportTickets(currentPage, debouncedSearchTerm);

  const { stats, error: statsError, refetch: refetchStats } = useSupportStats();

  const { updateTicket } = useUpdateSupportTicket();

  useEffect(() => {
    if (ticketsError) {
      toast.error("Failed to load tickets");
      console.error("Error fetching tickets:", ticketsError);
    }
    if (statsError) {
      toast.error("Failed to load support stats");
      console.error("Error fetching stats:", statsError);
    }
  }, [ticketsError, statsError]);

  const handleStatusChange = async (ticketId, newStatus) => {
    setSelectedTicketId(ticketId);

    try {
      await updateTicket(ticketId, { status: newStatus });
      toast.success("Ticket status updated successfully!");
      refetchTickets();
      refetchStats();
    } catch (error) {
      toast.error("Failed to update ticket status");
      console.error("Error updating status:", error);
    } finally {
      setSelectedTicketId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <PageHeader
        title="Support Tickets"
        description="Manage customer support tickets. Update the status of each ticket as needed."
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Tickets"
            value={stats.total_tickets}
            icon={TrendingUp}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Open Tickets"
            value={stats.open_tickets}
            icon={HelpCircle}
            color="#ed6c02"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="In Progress"
            value={stats.in_progress_tickets}
            icon={Users}
            color="#0288d1"
          />
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search tickets by number, subject, customer name or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={20} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, maxWidth: 600 }}
      />

      {/* Tickets Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {supportTickets.map((ticket) => (
              <TableRow
                key={ticket.id}
                hover
                onClick={() => navigate(`/admin/support/${ticket.id}`)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {ticket.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ticket.ticket_number}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={ticket.customer?.avatar}
                      sx={{ width: 32, height: 32 }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="bodySm" sx={{ fontWeight: 500 }}>
                        {ticket.customer?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ticket.customer?.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="bodySm">
                    {ticket.priority.charAt(0).toUpperCase() +
                      ticket.priority.slice(1)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="bodySm">
                    {formatDate(ticket.created_at)}
                  </Typography>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Select
                    value={ticket.status}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleStatusChange(ticket.id, e.target.value);
                    }}
                    size="small"
                    disabled={selectedTicketId === ticket.id}
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {supportTickets.length === 0 && (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <HelpCircle size={48} color="gray" style={{ marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary">
            No tickets found
          </Typography>
          <Typography variant="bodySm" color="text.secondary">
            {debouncedSearchTerm
              ? "No tickets found matching your search"
              : "No tickets available"}
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={pagination.totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </>
  );
}

export default Support;
